/**
 * BacktestSparkline.jsx — Phase N (19.10 Live Accuracy Bridge)
 *
 * Small inline SVG line chart of the last 30 nightly backtest
 * snapshots' direction accuracy. Reads from `/api/backtest/snapshots`.
 * Hover reveals the exact number for each day. Reduced-motion aware.
 *
 * Phase R (19.15) — source-aware rendering:
 *   snapshots carry a `source` of "live" (real nightly capture) or
 *   "synthetic" (v5.2 demo seed). Live points render as filled dots,
 *   synthetic points as hollow rings. As real captures accumulate the
 *   sparkline visibly transitions from hollow to filled. `showLegend`
 *   opts into a small "● live · ○ synthetic" caption below the chart.
 *
 * `variant`:
 *   "light" (default) — for placement on light surfaces (Methodology, cards).
 *   "dark"            — for placement on `bg-deep` (Landing AccuracyStats section).
 */
import { useEffect, useState } from "react";
import { BACKEND_URL } from "../../lib/api";

async function fetchSnapshots() {
    const res = await fetch(`${BACKEND_URL}/api/backtest/snapshots?limit=30`);
    if (!res.ok) throw new Error(`backtest-snapshots ${res.status}`);
    return await res.json();
}

const PALETTE = {
    light: {
        line:       "#1A237E",
        liveFill:   "#1A237E",
        liveStroke: "#1A237E",
        synthFill:  "#FFFFFF",
        synthStroke:"#1A237E",
        text:       "#4B5563",
        legendMuted:"#6B7280",
        emptyBg:    "#F4F6F8",
        emptyText:  "#94A3B8",
    },
    dark: {
        line:       "#FFFFFF",
        liveFill:   "#FFFFFF",
        liveStroke: "#FFFFFF",
        synthFill:  "transparent",
        synthStroke:"#FFFFFF",
        text:       "rgba(255,255,255,0.72)",
        legendMuted:"rgba(255,255,255,0.55)",
        emptyBg:    "rgba(255,255,255,0.06)",
        emptyText:  "rgba(255,255,255,0.55)",
    },
};

export default function BacktestSparkline({
    height = 40,
    width = 240,
    testId = "backtest-sparkline",
    variant = "light",
    showLegend = false,
}) {
    const [state, setState] = useState({ loading: true, snaps: [] });
    const [hover, setHover] = useState(null);
    const palette = PALETTE[variant] || PALETTE.light;

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await fetchSnapshots();
                if (!cancelled) setState({ loading: false, snaps: r.snapshots || [] });
            } catch {
                if (!cancelled) setState({ loading: false, snaps: [] });
            }
        })();
        return () => { cancelled = true; };
    }, []);

    if (state.loading) {
        return (
            <div style={{ height, width, background: palette.emptyBg, borderRadius: 4 }} data-testid={`${testId}-loading`} />
        );
    }
    if (state.snaps.length < 2) {
        return (
            <div
                data-testid={`${testId}-empty`}
                style={{
                    height, width,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: palette.emptyBg, borderRadius: 4,
                    fontSize: 10, color: palette.emptyText,
                    letterSpacing: "0.08em", textTransform: "uppercase",
                }}
            >
                Awaiting snapshots
            </div>
        );
    }

    const values = state.snaps.map((s) => Number(s.accuracy_pct) || 0);
    const min = Math.max(0, Math.min(...values) - 5);
    const max = Math.min(100, Math.max(...values) + 5);
    const range = Math.max(1, max - min);
    const pad = 4;
    const w = width, h = height;
    const xStep = (w - pad * 2) / Math.max(1, values.length - 1);
    const points = values.map((v, i) => {
        const x = pad + i * xStep;
        const y = h - pad - ((v - min) / range) * (h - pad * 2);
        return [x, y];
    });
    const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
    const last = state.snaps[state.snaps.length - 1];
    const liveCount = state.snaps.filter((s) => (s.source || "synthetic") === "live").length;
    const synthCount = state.snaps.length - liveCount;
    return (
        <div style={{ display: "inline-block" }} data-testid={`${testId}-root`}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <svg
                    width={w} height={h}
                    viewBox={`0 0 ${w} ${h}`}
                    role="img"
                    aria-label={`Accuracy trend over the last ${state.snaps.length} days, most recent value ${last?.accuracy_pct?.toFixed?.(1)}%`}
                    data-testid={testId}
                    style={{ display: "block" }}
                    onMouseLeave={() => setHover(null)}
                >
                    <path d={path} fill="none" stroke={palette.line} strokeWidth="1.5" />
                    {points.map(([x, y], i) => {
                        const snap = state.snaps[i];
                        const isLive = (snap?.source || "synthetic") === "live";
                        const isHover = i === hover;
                        return (
                            <circle
                                key={i}
                                cx={x} cy={y}
                                r={isHover ? 3.8 : 2.4}
                                fill={isLive ? palette.liveFill : palette.synthFill}
                                stroke={isLive ? palette.liveStroke : palette.synthStroke}
                                strokeWidth="1.5"
                                onMouseEnter={() => setHover(i)}
                                data-testid={`${testId}-point-${i}`}
                                data-source={isLive ? "live" : "synthetic"}
                                style={{ cursor: "pointer" }}
                            />
                        );
                    })}
                </svg>
                <span
                    style={{ fontSize: 11, color: palette.text, fontFamily: "'DM Mono', ui-monospace, monospace" }}
                    data-testid={`${testId}-value`}
                >
                    {hover != null && state.snaps[hover]
                        ? `${state.snaps[hover].accuracy_pct.toFixed(1)}% · ${state.snaps[hover].day_key}`
                        : `Latest ${last?.accuracy_pct?.toFixed?.(1)}%`}
                </span>
            </div>
            {showLegend && (
                <div
                    style={{
                        marginTop: 6,
                        fontSize: 10,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: palette.legendMuted,
                        fontFamily: "'DM Mono', ui-monospace, monospace",
                        display: "flex",
                        gap: 12,
                        alignItems: "center",
                    }}
                    data-testid={`${testId}-legend`}
                >
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                        <span
                            style={{
                                width: 7, height: 7, borderRadius: 999,
                                background: palette.liveFill,
                                border: `1.5px solid ${palette.liveStroke}`,
                                display: "inline-block",
                            }}
                        />
                        Live · {liveCount}
                    </span>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                        <span
                            style={{
                                width: 7, height: 7, borderRadius: 999,
                                background: palette.synthFill === "transparent" ? "transparent" : palette.synthFill,
                                border: `1.5px solid ${palette.synthStroke}`,
                                display: "inline-block",
                            }}
                        />
                        Synthetic · {synthCount}
                    </span>
                </div>
            )}
        </div>
    );
}
